// Depredated:
import React, { useEffect, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, Box, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import toast from 'react-hot-toast'
import { AxiosError } from 'axios'
import { LoadingButton } from '@mui/lab'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useGetBrandTeam } from '../../hooks/useGetBrandTeam'
import { useUser } from '../../../../hooks/useUser'
import { BrandTeamMember } from './BrandTeamMember'
import { useRemoveBrandTeam } from '../../hooks/userRemoveBrandTeam'
import { changeBrandTeamAdmin } from '../../../../api/endpoints'
import axiosInstant from '../../../../api'
import { useBoundStore } from '../../../../store'
import { TeamMemberModal } from '../../team/TeamMemberModal'
import { DotLottleLoader } from '../../../../components/Loader'
import { useWorkspaceBrandId } from '../../../../hooks/useWorkspaceBrandId';
import { useNetworkScope } from '../../../../hooks/useNetworkScope';

export function BrandTeamSettings() {
  const [dialogState, setDialogState] = useState(false)
  const [pendingUser, setPendingUser] = useState<any>(null)
  const [pendingAdmin, setPendingAdmin] = useState<any>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const { user, role, brand } = useUser()
  // TODO: really?
  const workspaceBrandId = useWorkspaceBrandId()
  const { brandName: workspaceBrandName } = useNetworkScope()
  const brandId: number = workspaceBrandId || (brand ? brand[0].id : 0)
  const brandName: string = (workspaceBrandId && workspaceBrandName) || (brand ? brand[0].name : '')
  const { brandTeam, getBrandTeam, loading } = useGetBrandTeam(brandId)
  const { removeFromBrandTeam, loading: removing } = useRemoveBrandTeam(brandId)
  const updateUserInfo = useBoundStore((state) => state.updateUserInfo)

  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  // /settings is a personal page and stays at the root (TDC-1609).
  if (role === 'BRAND_MEMBER') navigate('/settings')

  useEffect(() => {
    getBrandTeam()
  }, [])

  useEffect(() => {
    const shouldOpen = searchParams.get('new') === 'true'
    if (shouldOpen) {
      handleAddMember()
      const next = new URLSearchParams(searchParams.toString())
      next.delete('new')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams])

  const removeTeamMember = async (userData: any) => {
    setPendingUser(userData)
    setDialogState(true)
  }

  const changeAdminTeamMember = async (userData: any) => {
    setPendingAdmin(userData)
    setDialogState(true)
  }

  const changeBrandAdminAction = async () => {
    if (!pendingAdmin) return
    try {
      await axiosInstant.post(changeBrandTeamAdmin(brandId.toString(), pendingAdmin.userid.toString()), {})
      await updateUserInfo()
      setDialogState(false)
      toast.success('User has been made the brand\'s admin.')
      getBrandTeam()
    } catch (error) {
      toast.error((error as AxiosError).message)
    }
  }

  const removeFromBrandAction = async () => {
    if (!pendingUser) return
    try {
      await removeFromBrandTeam(pendingUser.userid.toString())
      setDialogState(false)
      toast.success('User has been removed from brand team.')
      getBrandTeam()
    } catch (error) {
      toast.error((error as AxiosError).message)
    }
  }
  const handleAddMember = () => {
    setSelectedUser(null)
    setIsModalOpen(true)
  }

  const handleEditMember = (user: any) => {
    setSelectedUser(user)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedUser(null)
  }

  const handleCopyInviteLink = async () => {
    const inviteLink = `https://portal.thedesirecompany.com/register/${encodeURI(brandName)}`
    try {
      await navigator.clipboard.writeText(inviteLink)
      toast.success('Invite link copied to clipboard!')
    } catch (error) {
      const textArea = document.createElement('textarea')
      textArea.value = inviteLink
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      toast.success('Invite link copied to clipboard!')
    }
  }

  return (
    <>
      <Box marginBottom={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
          <Typography variant="h4" className="font-[CircularStd] text-5xl mr-1">
            Team Members
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddMember}
            sx={{
              backgroundColor: 'var(--accent)',
              '&:hover': {
                backgroundColor: '#9a7440',
              },
            }}
          >
            New User
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <DotLottleLoader />
          </Box>
        ) : (
          <Stack spacing={2}>
            {brandTeam.filter(member => member != null).map((member, index) => (
              <BrandTeamMember
                key={member.userid || index}
                data={member}
                onDelete={removeTeamMember}
                onChangeAdmin={changeAdminTeamMember}
                onEdit={() => handleEditMember(member)}
              />
            ))}
          </Stack>
        )}
      </Box>

      {pendingUser && (
        <Dialog open={dialogState} onClose={() => setDialogState(false)} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title">Remove this user</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to remove <strong>{pendingUser.displayName}</strong> from your team?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogState(false)} disabled={removing}>
              Disagree
            </Button>
            <LoadingButton loading={removing} onClick={() => removeFromBrandAction()} autoFocus>
              Agree
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}

      {pendingAdmin && (
        <Dialog open={dialogState} onClose={() => setDialogState(false)} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title">Change admin user</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to make <strong>{pendingAdmin.displayName}</strong> the admin user for your team?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogState(false)} disabled={removing}>
              Disagree
            </Button>
            <LoadingButton loading={removing} onClick={() => changeBrandAdminAction()} autoFocus>
              Agree
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}

      <TeamMemberModal
        open={isModalOpen}
        onClose={handleCloseModal}
        user={selectedUser}
        brandId={brandId}
        onSuccess={getBrandTeam}
      />
    </>
  )
}
